// Neural beat tracking — the timeline behind the song-locked metronome and
// the chord snapping. Replaces the hand-tuned DSP tracker, which failed a
// different way on every real song (99/132/191 BPM on 77–110 material); the
// model here is the current best published beat tracker and reads the FULL
// MIX, so sparse-drums songs stop being a failure mode.
//
// Same beta plumbing as chords: own Cache Storage bucket, worker per run,
// gated callers. The model file is ~87 MB fetched once and cached.

import { cachedEngineBytes, putEngineBytes, readModelBody, isModelParseError, dropEngineBytes } from './modelCache';

export const BEATS_MODEL_URL = '/app/beats-v1.onnx';
const BEATS_CACHE = 'rl-beats-v1';
const ANALYSIS_SR = 22050;

export interface NeuralBeats {
  beats: number[]; // seconds, mixer-window time — follows the human drummer
  downbeats: number[]; // subset of beats that are bar "one"s
}

async function modelBytes(): Promise<Uint8Array> {
  const hit = await cachedEngineBytes(BEATS_MODEL_URL, BEATS_CACHE);
  if (hit) return hit;
  const res = await fetch(BEATS_MODEL_URL);
  if (!res.ok) throw new Error(`beats model fetch failed: ${res.status}`);
  const bytes = await readModelBody(res, BEATS_MODEL_URL);
  await putEngineBytes(BEATS_MODEL_URL, bytes, BEATS_CACHE);
  return bytes;
}

async function toAnalysisMono(
  left: Float32Array,
  right: Float32Array,
  sampleRate: number,
): Promise<Float32Array> {
  const frames = Math.min(left.length, right.length);
  const outLen = Math.max(1, Math.ceil((frames * ANALYSIS_SR) / sampleRate));
  const oac = new OfflineAudioContext(1, outLen, ANALYSIS_SR);
  const buf = oac.createBuffer(2, frames, sampleRate);
  buf.copyToChannel(left.subarray(0, frames) as Float32Array<ArrayBuffer>, 0);
  buf.copyToChannel(right.subarray(0, frames) as Float32Array<ArrayBuffer>, 1);
  const src = oac.createBufferSource();
  src.buffer = buf;
  src.connect(oac.destination);
  src.start();
  const rendered = await oac.startRendering();
  return rendered.getChannelData(0);
}

/** Track beats + downbeats for a stereo PCM window. Null when too short. */
export async function detectBeatsNeural(
  left: Float32Array,
  right: Float32Array,
  sampleRate: number,
  onProgress?: (frac: number) => void,
): Promise<NeuralBeats | null> {
  const duration = Math.min(left.length, right.length) / sampleRate;
  if (duration < 8) return null;

  const [mono, model] = await Promise.all([
    toAnalysisMono(left, right, sampleRate),
    modelBytes(),
  ]);
  const modelBuf =
    model.byteOffset === 0 && model.byteLength === model.buffer.byteLength
      ? (model.buffer as ArrayBuffer)
      : (model.slice().buffer as ArrayBuffer);

  return new Promise((resolve, reject) => {
    const w = new Worker(new URL('../workers/beatWorker.ts', import.meta.url), {
      type: 'module',
    });
    const bail = (err: unknown) => {
      w.terminate();
      reject(err instanceof Error ? err : new Error(String(err)));
    };
    w.onerror = (e) => bail(e.message || 'beat worker failed');
    w.onmessage = (e: MessageEvent) => {
      const m = e.data as
        | { type: 'progress'; done: number; total: number }
        | { type: 'done'; beats: number[]; downbeats: number[] }
        | { type: 'error'; message: string };
      if (m.type === 'progress') onProgress?.(m.done / m.total);
      else if (m.type === 'done') {
        w.terminate();
        resolve(m.beats.length >= 4 ? { beats: m.beats, downbeats: m.downbeats } : null);
      } else if (m.type === 'error') {
        // A cached model ORT can't parse fails every later run too: drop it
        // first, so the next analysis downloads a fresh copy.
        if (isModelParseError(m.message)) void dropEngineBytes(BEATS_MODEL_URL, BEATS_CACHE).then(() => bail(m.message));
        else bail(m.message);
      }
    };
    w.postMessage({ pcm: mono, model: modelBuf, duration }, [
      mono.buffer as ArrayBuffer,
      modelBuf,
    ]);
  });
}
